import _get from 'lodash/get'
import _slice from 'lodash/slice'
import _isNumber from 'lodash/isNumber'
import _parseInt from 'lodash/parseInt'
import _startsWith from 'lodash/startsWith'
import {
  Map, List, Set, fromJS,
} from 'immutable'
import {
  client as apolloClient,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
  APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
} from '../apollo-graphql/index'
import {
  getVideos,
  getSeries,
  getTerms,
  getPromotions,
  getProducts,
} from '../node/index'
import { TYPE_CONTENT_PRODUCT } from '../content-type/index'
import { CONTENT_IMAGE_LIST } from '../content/index'
import {
  URL_TOPICS,
  URL_HOME,
  URL_YOGA_TEACHERS,
} from '../url/constants'
import { get as getConfig } from '../../config/index'
import { RESOLVER_TYPE_SHARE } from '../resolver/types'
import { setMultiplePmLists as setMultiplePmListsAction } from '../pm-list/actions'
import {
  setPmSection as setPmSectionAction,
  setMultiplePmSections as setMultiplePmSectionsAction,
  setPmSectionError as setPmSectionErrorAction,
  setPmSectionDone as setPmSectionDoneAction,
  setPmSectionProcessing as setPmSectionProcessingAction,
} from './actions'
import GET_PM_SECTION_LIST from './query'
import {
  getPmSectionProcessing,
  getPmSectionErrorCreated,
  getPmSectionDataCreated,
  preventSectionDataLoad,
  TYPE_PROMOTION,
  GET_PM_SECTION_ERROR_CACHE_TIME,
} from '.'

const config = getConfig()
const tilesV2GraphqlActive = !!_get(config, ['features', 'tilesV2Graphql'])

/**
 * Get multiple page manger sections and dispatch associated actions
 * @param {Object} options The options
 * @param {import('redux').Dispatch} options.dispatch Redux dispatch function
 * @param {String} options.language The request language
 * @param {Map} options.auth The auth state
 * @param {Map} options.resolver The resolver state
 * @param {Map} options.pmSection The page manager section state
 * @param {String[]|List|Set} options.sectionIds The page manager section ids
 * @param {Number} [options.limit] A limit for the number of page manager sections
 */
export async function getPmSectionMultiple (options = {}) {
  const {
    dispatch,
    language,
    auth = Map(),
    resolver = Map(),
    pmSection = Map(),
    limit,
  } = options
  let { sectionIds = [] } = options
  try {
    if (List.isList(sectionIds) || Set.isSet(sectionIds)) {
      sectionIds = sectionIds.toArray()
    }

    if (!sectionIds || sectionIds.length === 0) {
      return
    }

    if (limit) {
      sectionIds = _slice(sectionIds, 0, limit)
    }

    // Filter out sections that are already processing or recently loaded
    const sectionsToLoad = sectionIds.filter((sectionId) => {
      const processing = getPmSectionProcessing({ pmSection, sectionId })
      const errorCreated = getPmSectionErrorCreated({ pmSection, language, sectionId })
      const dataCreated = getPmSectionDataCreated({ pmSection, language, sectionId })
      const preventDataLoad = preventSectionDataLoad({ now: new Date(), dataCreated })

      if (processing) {
        return false
      }
      const errorTime = errorCreated.getTime() + GET_PM_SECTION_ERROR_CACHE_TIME
      if (errorCreated && new Date().getTime() < errorTime) {
        return false
      }
      if (preventDataLoad) {
        return false
      }
      return true
    })

    if (sectionsToLoad.length === 0) {
      return
    }

    const pageUrl = resolver.get('path', '')
    const isHome = pageUrl === URL_HOME
    const preventNodeFetch = tilesV2GraphqlActive && isHome

    const authToken = auth.get('jwt')
    const { data, error } = await apolloClient.query({
      query: GET_PM_SECTION_LIST,
      variables: {
        sectionIds: sectionsToLoad,
        contentLanguage: [language, '_primary'],
        imageType: CONTENT_IMAGE_LIST,
      },
      fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
      errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
      context: {
        jwt: authToken,
      },
    })

    // Handle GraphQL errors gracefully - check if we have partial data
    // GraphQL validation errors (like null for non-nullable field) can cause
    // the entire query to fail, but we should still try to process any data we got
    // Use safe access to check for data even when pmSectionList might be null
    const pmSections = _get(data, ['pmSectionList', 'pmSections'], [])

    // If we have errors but no sections, log the error but don't fail completely
    // This allows the page to continue functioning even when some sections fail to load
    if (error && pmSections.length === 0) {
      // Log error but don't block - allow page to continue
      sectionsToLoad.forEach((sectionId) => {
        dispatch(setPmSectionErrorAction(sectionId, error))
      })
      // Don't return early - allow any sections that did load to be processed
      // This handles the case where GraphQL returns null but we might have partial data
    }

    if (pmSections.length > 0) {
      // Store GraphQL response directly - no transformation
      // Section: { id, app: { id, type, adminTitle, data, visibility, ... }, pmList: [...] }
      // Reducer stores section.app as state[sectionId].data
      const sectionsToStore = pmSections.map((section) => ({
        id: section.id,
        app: section.app, // Store all GraphQL fields from app
        pmList: section.pmList, // Keep pmList for separate dispatch
      }))

      // Set multiple sections in Redux - store GraphQL response directly
      // Mark as done: true since GraphQL already returns complete data
      dispatch(setMultiplePmSectionsAction({
        sections: sectionsToStore,
        processing: false,
        done: true,
      }))

      // Extract all pmLists from all sections and set them in Redux
      // Filter out pmLists with null listItem ids to prevent GraphQL validation errors
      const allPmLists = []
      pmSections.forEach((section) => {
        if (section.pmList && section.pmList.length > 0) {
          section.pmList.forEach((pmList) => {
            // Filter out listItems with null ids before storing
            // This prevents GraphQL validation errors when the backend has bad data
            const filteredPmList = { ...pmList }
            if (filteredPmList.listItems && Array.isArray(filteredPmList.listItems)) {
              filteredPmList.listItems = filteredPmList.listItems.filter(
                (item) => item && item.id != null,
              )
            }
            allPmLists.push(filteredPmList)
          })
        }
      })
      if (allPmLists.length > 0) {
        dispatch(setMultiplePmListsAction({
          lists: allPmLists,
          processing: false,
        }))
      }

      // Process content from tabs for each section
      // Tabs are in section.app.data for certain section types
      pmSections.forEach((section) => {
        const appData = section.app || {}
        const sectionDataForTabs = appData.data || {}
        const tabs = sectionDataForTabs.tabs || []
        const contentList = tabs.flatMap((tab) => tab.tabContent || [])

        // Extract content IDs by type and dispatch node fetches
        const videosList = contentList.filter((v) => v.contentType === 'video')
        if (videosList.length > 0) {
          const ids = videosList.map((v) => v.contentId).filter(Boolean)
          if (ids.length > 0) {
            dispatch(getVideos({ ids, language, preventNodeFetch }))
          }
        }

        const seriesList = contentList.filter((v) => v.contentType === 'series')
        if (seriesList.length > 0) {
          const ids = seriesList.map((v) => Number(v.contentId)).filter(Boolean)
          if (ids.length > 0) {
            dispatch(getSeries({ ids, language, preventNodeFetch }))
          }
        }

        const promotions = contentList.filter((v) => v.contentType === TYPE_PROMOTION)
        if (promotions.length > 0) {
          const ids = promotions.map((v) => v.contentId).filter(Boolean)
          if (ids.length > 0) {
            dispatch(getPromotions({ ids, language, preventNodeFetch: false }))
          }
        }

        const products = contentList.filter((v) => v.contentType === TYPE_CONTENT_PRODUCT)
        if (products.length > 0) {
          const ids = products.map((v) => v.contentId).filter(Boolean)
          if (ids.length > 0) {
            dispatch(getProducts({ ids, language, preventNodeFetch: false }))
          }
        }

        // Handle tags/terms for topics (contentType === 'tag')
        const tagsList = contentList.filter((v) => v.contentType === 'tag')
        if (tagsList.length > 0) {
          const ids = tagsList.map((v) => Number(v.contentId)).filter(Boolean)
          if (ids.length > 0) {
            dispatch(getTerms({
              ids, language, fetchAsNodes: true, contentType: 'tag',
            }))
          }
        }

        // Handle parent term fetch if needed (from footer label)
        // section.app.data is a union type, footer is only in PMSectionAppDataContentList
        const sectionAppData = section.app || {}
        const sectionDataForFooter = sectionAppData.data || {}
        const sectionDataImmutable = fromJS(sectionDataForFooter)
        const parentTermId = sectionDataImmutable.getIn(['footer', 'label']) ? _parseInt(sectionDataImmutable.getIn(['footer', 'label']), 10) : undefined
        if (parentTermId && _isNumber(parentTermId) && _startsWith(pageUrl, URL_TOPICS)) {
          dispatch(getTerms({
            ids: [parentTermId], language, fetchAsNodes: true, contentType: 'tag',
          }))
        }
      })
    }
  } catch (e) {
    // Handle errors for each section
    let ids = sectionIds
    if (List.isList(ids) || Set.isSet(ids)) {
      ids = ids.toArray()
    }
    ids.forEach((sectionId) => {
      dispatch(setPmSectionErrorAction(sectionId, e))
    })
  }
}

/**
 * Get one page manger section and dispatch associated actions
 * @param {Object} options The options
 * @param {import('redux').Dispatch} options.dispatch Redux dispatch function
 * @param {String} options.language The request language
 * @param {Map} options.auth The auth state
 * @param {Map} options.resolver The resolver state
 * @param {Map} options.pmSection The page manager section state
 * @param {String} options.sectionId The page manager section id
 */
export async function getPmSectionSingle (options = {}) {
  const {
    dispatch,
    language,
    auth = Map(),
    resolver = Map(),
    pmSection = Map(),
    sectionId,
    now = new Date(),
  } = options
  try {
    const processing = getPmSectionProcessing({ pmSection, sectionId })
    const errorCreated = getPmSectionErrorCreated({ pmSection, language, sectionId })
    const dataCreated = getPmSectionDataCreated({ pmSection, language, sectionId })
    const preventDataLoad = preventSectionDataLoad({ now, dataCreated })
    const isHome = resolver.get('path', '') === URL_HOME
    const isYogaTeachersPage = resolver.get('path', '') === URL_YOGA_TEACHERS
    const isSharePage = resolver.getIn(['data', 'type']) === RESOLVER_TYPE_SHARE

    if (processing) {
      return
    }
    // Don't get page manager screen if there was an error recently
    // eslint-disable-next-line max-len
    if (errorCreated && now.getTime() < (errorCreated.getTime() + GET_PM_SECTION_ERROR_CACHE_TIME)) {
      return
    }
    // Don't get page manager data if data was fetch recently
    if (preventDataLoad) {
      return
    }
    dispatch(setPmSectionProcessingAction(sectionId, true))

    const authToken = auth.get('jwt')
    const { data, error } = await apolloClient.query({
      query: GET_PM_SECTION_LIST,
      variables: {
        sectionIds: [sectionId],
        contentLanguage: [language, '_primary'],
        imageType: CONTENT_IMAGE_LIST,
      },
      fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
      errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
      context: {
        jwt: authToken,
      },
    })

    // Handle GraphQL errors gracefully - check if we have partial data
    // GraphQL validation errors (like null for non-nullable field) can cause
    // the entire query to fail, but we should still try to process any data we got
    // Use safe access to check for data even when pmSectionList might be null
    const pmSections = _get(data, ['pmSectionList', 'pmSections'], [])
    if (pmSections.length === 0) {
      // Only fail if we have no data at all
      if (error) {
        dispatch(setPmSectionErrorAction(sectionId, error))
      } else {
        dispatch(setPmSectionErrorAction(sectionId, new Error(`No section found for id: ${sectionId}`)))
      }
      return
    }

    const [section] = pmSections

    if (!section.app) {
      dispatch(setPmSectionErrorAction(
        sectionId,
        new Error(`Section ${sectionId} has no app data`),
      ))
      return
    }

    // Store GraphQL response directly - no transformation
    // Section: { id, app: { id, type, adminTitle, data, visibility, ... }, pmList: [...] }
    // Reducer stores section.app as state[sectionId].data
    const sectionData = {
      id: section.id,
      app: section.app, // Store all GraphQL fields from app
      pmList: section.pmList,
    }

    // Extract footer label from app.data (union type, footer only in PMSectionAppDataContentList)
    const appData = section.app || {}
    const sectionDataForFooter = appData.data || {}
    const sectionDataImmutable = fromJS(sectionDataForFooter)
    const parentTermId = sectionDataImmutable.getIn(['footer', 'label']) ? _parseInt(sectionDataImmutable.getIn(['footer', 'label']), 10) : undefined
    const pageUrl = resolver.get('path')
    const preventNodeFetch = (tilesV2GraphqlActive && isHome)
      || isYogaTeachersPage
      || isSharePage

    // If there is a term id in the footer, fetch the parent term data if this the topics page
    // This allows the data to be fetched for the parent category so it can link to a page
    // like the child terms. This should be refactored at some point, as getting a string id
    // from the footer is a bit hacky
    if (parentTermId && _isNumber(parentTermId) && _startsWith(pageUrl, URL_TOPICS)) {
      dispatch(getTerms({
        ids: [parentTermId], language, fetchAsNodes: true, contentType: 'tag',
      }))
    }

    dispatch(setPmSectionAction({
      sectionId,
      data: sectionData,
      processing: false,
      // Done will be set to true after all dependacies have loaded
      done: false,
    }))

    // Extract and set pmList data if present
    if (section.pmList && section.pmList.length > 0) {
      dispatch(setMultiplePmListsAction({
        lists: section.pmList,
        processing: false,
      }))
    }

    // Process content from tabs
    // Tabs are in section.app.data for certain section types
    const sectionAppData = section.app || {}
    const sectionDataForTabs = sectionAppData.data || {}
    const tabs = sectionDataForTabs.tabs || []
    const contentList = tabs.flatMap((tab) => tab.tabContent || [])

    // Extract content IDs by type and dispatch node fetches
    const videosList = contentList.filter((v) => v.contentType === 'video')
    if (videosList.length > 0) {
      const ids = videosList.map((v) => v.contentId).filter(Boolean)
      if (ids.length > 0) {
        dispatch(getVideos({ ids, language, preventNodeFetch }))
      }
    }

    const seriesList = contentList.filter((v) => v.contentType === 'series')
    if (seriesList.length > 0) {
      const ids = seriesList.map((v) => Number(v.contentId)).filter(Boolean)
      if (ids.length > 0) {
        dispatch(getSeries({ ids, language, preventNodeFetch }))
      }
    }

    const promotions = contentList.filter((v) => v.contentType === TYPE_PROMOTION)
    if (promotions.length > 0) {
      const ids = promotions.map((v) => v.contentId).filter(Boolean)
      if (ids.length > 0) {
        dispatch(getPromotions({ ids, language, preventNodeFetch }))
      }
    }

    const products = contentList.filter((v) => v.contentType === TYPE_CONTENT_PRODUCT)
    if (products.length > 0) {
      const ids = products.map((v) => v.contentId).filter(Boolean)
      if (ids.length > 0) {
        dispatch(getProducts({ ids, language, preventNodeFetch }))
      }
    }

    // Set done to true now that all dependacies are loaded
    dispatch(setPmSectionDoneAction(sectionId, true))
  } catch (e) {
    dispatch(setPmSectionErrorAction(sectionId, e))
  }
}
